import { Network, NetworkValidationResponse, NetworkCondition, NetworkSelectionState, SignupFormData } from '../types';
import { 
  NETWORKS as CONFIG_NETWORKS, 
  getNetworkById, 
  getNetworkCondition, 
  shouldRedirectToSales, 
  requiresApproval,
  getSalesRedirectUrl,
  getConditionalMessage,
  DEFAULT_NETWORK_ID 
} from '../config/networks';

/**
 * Network Service
 * 
 * Handles all network-related operations, validation, and conditional routing logic
 */

export class NetworkService {
  /**
   * Get all available networks
   */
  static getAvailableNetworks(): Network[] {
    return CONFIG_NETWORKS;
  }

  /**
   * Validate network selection
   */
  static validateNetwork(networkId: string): { isValid: boolean; error?: string } {
    if (!networkId) {
      return { isValid: false, error: 'Please select a network or club' };
    }

    const network = getNetworkById(networkId);
    if (!network) {
      return { isValid: false, error: 'Invalid network selection' };
    }

    return { isValid: true };
  }

  /**
   * Process network selection and return conditional state
   */
  static processNetworkSelection(networkId: string): NetworkSelectionState {
    const network = getNetworkById(networkId);
    
    if (!network) {
      return {
        selectedNetworks: [],
        showConditionalMessage: false,
        conditionalAction: null,
        canProceed: false
      };
    }

    const condition = getNetworkCondition(networkId);
    const message = getConditionalMessage(networkId);
    
    // Determine if user can proceed with form
    const canProceed = !shouldRedirectToSales(networkId);
    
    return {
      selectedNetworks: [network],
      showConditionalMessage: !!(message || condition),
      conditionalAction: condition,
      canProceed
    };
  }

  /**
   * Process multiple network selections and return conditional state
   */
  static processMultipleNetworkSelection(networkIds: string[]): NetworkSelectionState {
    if (networkIds.length === 0) {
      return {
        selectedNetworks: [],
        showConditionalMessage: false,
        conditionalAction: null,
        canProceed: false
      };
    }

    const networks = networkIds
      .map(id => getNetworkById(id))
      .filter(Boolean) as Network[];
    
    // Check if any networks require special handling
    const hasRedirectNetwork = networks.some(n => shouldRedirectToSales(n.id));
    const hasApprovalNetwork = networks.some(n => n.requiresApproval);
    const hasCustomOnboarding = networks.some(n => !!n.customOnboardingPath);
    
    let showMessage = false;
    let canProceed = true;
    let conditionalAction = null;
    
    if (hasRedirectNetwork) {
      showMessage = true;
      canProceed = false; // Can't proceed if redirecting to sales
      const redirectNetwork = networks.find(n => shouldRedirectToSales(n.id));
      conditionalAction = getNetworkCondition(redirectNetwork!.id);
    } else if (hasApprovalNetwork) {
      showMessage = true;
      canProceed = true; // Can proceed but needs approval
      const approvalNetwork = networks.find(n => n.requiresApproval);
      conditionalAction = getNetworkCondition(approvalNetwork!.id);
    } else if (hasCustomOnboarding) {
      showMessage = true;
      canProceed = true;
    }
    
    return {
      selectedNetworks: networks,
      showConditionalMessage: showMessage,
      conditionalAction,
      canProceed
    };
  }

  /**
   * Handle network-specific routing logic
   */
  static handleNetworkRouting(networkId: string): {
    action: 'redirect' | 'continue' | 'require_approval';
    url?: string;
    message?: string;
    data?: Record<string, any>;
  } {
    const network = getNetworkById(networkId);
    
    if (!network) {
      return { action: 'continue' };
    }

    // Check for sales redirect
    if (shouldRedirectToSales(networkId)) {
      const redirectUrl = getSalesRedirectUrl(networkId);
      return {
        action: 'redirect',
        url: redirectUrl || undefined,
        message: getConditionalMessage(networkId) || undefined,
        data: {
          networkId,
          networkName: network.name,
          redirectReason: 'sales_consultation',
          utm_source: 'signup_form',
          utm_campaign: `network_${networkId}`
        }
      };
    }

    // Check for approval requirement
    if (requiresApproval(networkId)) {
      return {
        action: 'require_approval',
        message: getConditionalMessage(networkId) || undefined,
        data: {
          networkId,
          networkName: network.name,
          approvalRequired: true,
          customOnboarding: !!network.customOnboardingPath
        }
      };
    }

    // Standard continue flow
    return {
      action: 'continue',
      message: getConditionalMessage(networkId) || undefined,
      data: {
        networkId,
        networkName: network.name,
        customOnboarding: !!network.customOnboardingPath
      }
    };
  }

  /**
   * Get HubSpot settings for a network
   */
  static getHubSpotSettings(networkId: string): Record<string, any> | null {
    const network = getNetworkById(networkId);
    return network?.hubspotSettings || null;
  }

  /**
   * Prepare form data with network-specific enhancements
   */
  static enhanceFormDataWithNetwork(formData: SignupFormData): SignupFormData & {
    networkData?: {
      networks: Network[];
      primaryNetwork?: Network;
      conditions?: NetworkCondition[];
      hubspotSettings?: Record<string, any>;
      requiresApproval: boolean;
      customOnboarding: boolean;
    };
  } {
    if (!formData.networkIds || formData.networkIds.length === 0) {
      return formData;
    }

    const networks = formData.networkIds
      .map(id => getNetworkById(id))
      .filter(Boolean) as Network[];
    
    if (networks.length === 0) {
      return formData;
    }

    // Use first network as primary for legacy compatibility
    const primaryNetwork = networks[0];
    const conditions = formData.networkIds
      .map(id => getNetworkCondition(id))
      .filter(Boolean) as NetworkCondition[];
    
    const hubspotSettings = NetworkService.getHubSpotSettings(primaryNetwork.id);
    const requiresApproval = networks.some(n => n.requiresApproval);
    const customOnboarding = networks.some(n => !!n.customOnboardingPath);

    return {
      ...formData,
      networkData: {
        networks,
        primaryNetwork,
        conditions: conditions.length > 0 ? conditions : undefined,
        hubspotSettings: hubspotSettings || undefined,
        requiresApproval,
        customOnboarding
      }
    };
  }

  /**
   * Track network selection analytics
   */
  static trackNetworkSelection(networkId: string, action: 'selected' | 'redirected' | 'approved'): void {
    const network = getNetworkById(networkId);
    
    if (!network) {
      return;
    }

    // Analytics tracking (integrate with your analytics service)
    const eventData = {
      event: `network_${action}`,
      networkId,
      networkName: network.name,
      networkDisplayName: network.displayName,
      requiresApproval: requiresApproval(networkId),
      redirectsToSales: shouldRedirectToSales(networkId),
      customOnboarding: !!network.customOnboardingPath,
      timestamp: new Date().toISOString()
    };

    // Log for development
    console.log('🔗 Network Analytics:', eventData);

    // In production, send to your analytics service:
    // analytics.track(eventData);
    // window.gtag?.('event', eventData.event, eventData);
  }
}

/**
 * Legacy functions for backwards compatibility
 */

/**
 * Get all available networks (async version for API compatibility)
 */
export const getAvailableNetworks = async (): Promise<NetworkValidationResponse> => {
  try {
    return {
      networks: CONFIG_NETWORKS,
      defaultNetworkId: DEFAULT_NETWORK_ID,
      message: 'Networks loaded successfully'
    };
  } catch (error) {
    console.error('Error loading networks:', error);
    return {
      networks: [CONFIG_NETWORKS[0]], // Fallback to first network
      defaultNetworkId: DEFAULT_NETWORK_ID,
      error: 'Failed to load all networks, showing default options'
    };
  }
};

/**
 * Get all available networks (synchronous version)
 */
export const getAllNetworks = (): Network[] => {
  return CONFIG_NETWORKS;
};

/**
 * Get a specific network by ID
 */
export { getNetworkById };

/**
 * Check if a network requires approval
 */
export const networkRequiresApproval = (networkId: string): boolean => {
  return requiresApproval(networkId);
};

/**
 * Get custom onboarding path for a network
 */
export const getNetworkOnboardingPath = (networkId: string): string | null => {
  const network = getNetworkById(networkId);
  return network?.customOnboardingPath || null;
};

/**
 * Get HubSpot settings for a network
 */
export const getNetworkHubSpotSettings = (networkId: string): Network['hubspotSettings'] => {
  const network = getNetworkById(networkId);
  return network?.hubspotSettings;
};

/**
 * Validate if user can access a specific network
 */
export const validateNetworkAccess = async (networkId: string, fcaNumber?: string): Promise<{
  hasAccess: boolean;
  reason?: string;
  requiresApproval?: boolean;
}> => {
  const network = getNetworkById(networkId);
  
  if (!network) {
    return {
      hasAccess: false,
      reason: 'Network not found'
    };
  }

  // For direct signup, everyone has access
  if (networkId === DEFAULT_NETWORK_ID) {
    return {
      hasAccess: true,
      requiresApproval: false
    };
  }

  // For networks that redirect to sales, allow selection but indicate special handling
  if (shouldRedirectToSales(networkId)) {
    return {
      hasAccess: true,
      requiresApproval: false,
      reason: 'Will be redirected to sales team for specialized setup'
    };
  }

  // For partner networks, basic validation
  if (!fcaNumber) {
    return {
      hasAccess: true,
      requiresApproval: network.requiresApproval,
      reason: network.requiresApproval 
        ? 'Network membership will be verified during approval process'
        : 'Access granted'
    };
  }

  return {
    hasAccess: true,
    requiresApproval: network.requiresApproval,
    reason: network.requiresApproval 
      ? 'Network membership will be verified during approval process'
      : 'Direct access granted'
  };
};

// New exports for conditional routing
export const validateNetworkSelection = NetworkService.validateNetwork;
export const processNetworkSelection = NetworkService.processNetworkSelection;
export const processMultipleNetworkSelection = NetworkService.processMultipleNetworkSelection;
export const handleNetworkRouting = NetworkService.handleNetworkRouting;
export const trackNetworkSelection = NetworkService.trackNetworkSelection;
export const enhanceFormDataWithNetwork = NetworkService.enhanceFormDataWithNetwork;

// Default export
const networkService = {
  getAvailableNetworks,
  getAllNetworks,
  getNetworkById,
  networkRequiresApproval,
  getNetworkOnboardingPath,
  getNetworkHubSpotSettings,
  validateNetworkAccess,
  validateNetworkSelection,
  processNetworkSelection,
  handleNetworkRouting,
  trackNetworkSelection,
  enhanceFormDataWithNetwork
};

export default networkService;
